import { useMemo, useState } from 'react';
import type { Organization } from '../lib/api';
import type { TeacherTask } from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { formatDateShort, formatRelative } from './dates';

export interface HomeworkViewProps {
  org: Organization;
  tasks: TeacherTask[];
  loading: boolean;
  /** True for the owner, whose list mixes their own tasks with the teachers'. */
  showsAuthors: boolean;
  /** False while nobody is assignable — no students linked, none in the org. */
  canCreate: boolean;
  onCreate: () => void;
  onOpenTask: (taskId: string) => void;
}

type Filter = 'open' | 'review' | 'all';

/**
 * Homework as a full authoring page for teachers and the owner.
 *
 * Defaulted to what is still outstanding: this screen answers "who has not
 * handed it in yet", not "what did I set last term".
 */
export function HomeworkView({
  org,
  tasks,
  loading,
  showsAuthors,
  canCreate,
  onCreate,
  onOpenTask,
}: HomeworkViewProps) {
  const [filter, setFilter] = useState<Filter>(showsAuthors ? 'open' : 'review');
  const [subject, setSubject] = useState('');

  const subjects = useMemo(() => {
    const names = new Set<string>();
    for (const task of tasks) if (task.subject) names.add(task.subject);
    return [...names].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [tasks]);

  const visible = useMemo(() => {
    const list = tasks.filter((task) => {
      if (subject && task.subject !== subject) return false;
      if (filter === 'all') return true;
      if (filter === 'review') return task.assignments.some((a) => a.needsReview);
      return task.assignments.some((a) => !a.done);
    });
    return list.sort((a, b) => {
      const ar = a.assignments.some((x) => x.needsReview) ? 0 : 1;
      const br = b.assignments.some((x) => x.needsReview) ? 0 : 1;
      if (ar !== br) return ar - br;
      return (a.dueAt ?? '9999').localeCompare(b.dueAt ?? '9999');
    });
  }, [filter, subject, tasks]);

  const outstanding = tasks.filter((task) => task.assignments.some((a) => !a.done)).length;
  const toReview = tasks.filter((task) => task.assignments.some((a) => a.needsReview)).length;
  const overdue = tasks.filter((task) => {
    if (!task.dueAt) return false;
    if (!task.assignments.some((a) => !a.done)) return false;
    return new Date(task.dueAt) < new Date();
  }).length;

  return (
    <section className="hw-page">
      <header className="hw-hero">
        <div className="hw-hero-copy">
          <p className="hw-kicker">
            <Icon name="clipboard" size={14} />
            {org.name}
          </p>
          <h1>Домашняя работа</h1>
          <p className="hw-hero-lead">
            {loading
              ? 'Загрузка…'
              : tasks.length === 0
                ? 'Выберите ученика, опишите задание и приложите файлы — оно появится у него в списке и в календаре.'
                : toReview > 0
                  ? `${toReview} ${toReview === 1 ? 'работа ждёт' : 'работ ждут'} проверки.${outstanding > toReview ? ` Ещё ${outstanding - toReview} не сдано.` : ''}`
                  : outstanding === 0
                    ? 'Все текущие задания сданы.'
                    : `${outstanding} ещё не сдано${overdue > 0 ? `, из них ${overdue} с просроченным сроком` : ''}.`}
          </p>
        </div>

        <div className="hw-hero-actions">
          {!loading && tasks.length > 0 && (
            <div className="hw-stats" aria-label="Сводка по заданиям">
              <div className="hw-stat">
                <strong>{tasks.length}</strong>
                <span>заданий</span>
              </div>
              <div className="hw-stat">
                <strong>{outstanding}</strong>
                <span>не сдано</span>
              </div>
              <div className={`hw-stat ${toReview > 0 ? 'is-warn' : ''}`}>
                <strong>{toReview}</strong>
                <span>на проверке</span>
              </div>
              <div className={`hw-stat ${overdue > 0 ? 'is-warn' : ''}`}>
                <strong>{overdue}</strong>
                <span>просрочено</span>
              </div>
            </div>
          )}
          <button type="button" className="primary-btn" disabled={!canCreate} onClick={onCreate}>
            <Icon name="plus" size={16} />
            Новое задание
          </button>
        </div>
      </header>

      {!canCreate && !loading && (
        <p className="hw-banner">
          <Icon name="users" size={15} />
          {showsAuthors
            ? 'Сначала добавьте учеников в организацию — задание нужно кому-то адресовать.'
            : 'За вами пока не закреплены ученики — это делает владелец организации.'}
        </p>
      )}

      {tasks.length > 0 && (
        <div className="hw-toolbar">
          <div className="seg-group" role="tablist" aria-label="Фильтр заданий">
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'open'}
              className={filter === 'open' ? 'is-active' : ''}
              onClick={() => setFilter('open')}
            >
              Не сдано
              <em>{outstanding}</em>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'review'}
              className={filter === 'review' ? 'is-active' : ''}
              onClick={() => setFilter('review')}
            >
              На проверке
              <em>{toReview}</em>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === 'all'}
              className={filter === 'all' ? 'is-active' : ''}
              onClick={() => setFilter('all')}
            >
              Все
              <em>{tasks.length}</em>
            </button>
          </div>
          {subjects.length > 0 && (
            <select
              className="hw-subject-filter"
              value={subject}
              aria-label="Предмет"
              onChange={(e) => setSubject(e.target.value)}
            >
              <option value="">Все предметы</option>
              {subjects.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {loading && (
        <div className="hw-grid" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="hw-card-skel" />
          ))}
        </div>
      )}

      {!loading && tasks.length === 0 && (
        <div className="hw-empty">
          <span className="hw-empty-icon" aria-hidden="true">
            <Icon name="clipboard" size={32} />
          </span>
          <h2>Заданий пока нет</h2>
          <p>
            Выберите ученика, напишите тему и описание, приложите фото или файлы — задание сразу
            появится у него и в календаре.
          </p>
          {canCreate && (
            <button type="button" className="primary-btn" onClick={onCreate}>
              <Icon name="plus" size={16} />
              Создать первое задание
            </button>
          )}
        </div>
      )}

      {!loading && tasks.length > 0 && visible.length === 0 && (
        <div className="hw-empty">
          <span className="hw-empty-icon is-ok" aria-hidden="true">
            <Icon name="check" size={32} />
          </span>
          <h2>Здесь пусто</h2>
          <p>Всё сдано или скрыто фильтром. Снимите фильтр, чтобы увидеть все задания.</p>
          <button type="button" className="ghost-btn" onClick={() => setFilter('all')}>
            Показать все
          </button>
        </div>
      )}

      {!loading && visible.length > 0 && (
        <ul className="hw-grid">
          {visible.map((task, index) => {
            const done = task.assignments.filter((a) => a.done).length;
            const total = task.assignments.length;
            const reviewCount = task.assignments.filter((a) => a.needsReview).length;
            const isOverdue =
              task.dueAt !== null && done < total && new Date(task.dueAt) < new Date();
            const photos = task.attachments.filter((f) => f.mime.startsWith('image/'));
            const progress = total === 0 ? 0 : Math.round((done / total) * 100);

            return (
              <li
                key={task.id}
                className={`hw-tile is-author ${isOverdue ? 'is-overdue' : done === total ? 'is-done' : 'is-later'}`}
                style={{ '--hw-i': index } as React.CSSProperties}
              >
                <button type="button" className="hw-tile-body" onClick={() => onOpenTask(task.id)}>
                  <div className="hw-tile-top">
                    {task.subject ? (
                      <span className="subject-badge">{task.subject}</span>
                    ) : (
                      <span className="hw-tone is-later">Без предмета</span>
                    )}
                    {reviewCount > 0 && (
                      <span className="hw-tone is-soon">На проверке · {reviewCount}</span>
                    )}
                    {isOverdue && <span className="hw-tone is-overdue">Просрочено</span>}
                    {!isOverdue && done === total && total > 0 && (
                      <span className="hw-tone is-done">Все сдали</span>
                    )}
                  </div>

                  <h2>{task.title}</h2>

                  {task.description && <p className="hw-tile-desc">{task.description}</p>}

                  <div className="hw-progress" aria-hidden="true">
                    <span style={{ '--p': progress / 100 } as React.CSSProperties} />
                  </div>
                  <p className="hw-progress-label">
                    сдали {done} из {total}
                  </p>

                  <div className="hw-tile-meta">
                    <span className={isOverdue ? 'is-overdue' : ''}>
                      <Icon name="clock" size={14} />
                      {task.dueAt
                        ? `${formatDateShort(task.dueAt)} · ${formatRelative(task.dueAt)}`
                        : 'без срока'}
                    </span>
                    {task.attachments.length > 0 && (
                      <span>
                        <Icon name="paperclip" size={14} />
                        {task.attachments.length}
                      </span>
                    )}
                    {showsAuthors && task.teacherName && (
                      <span>
                        <Icon name="graduation" size={14} />
                        {task.teacherName}
                      </span>
                    )}
                  </div>

                  <span className="hw-card-students">
                    {task.assignments.map((student) => (
                      <span
                        key={student.userId}
                        className={`task-chip ${student.done ? 'is-done' : ''} ${student.needsReview ? 'is-review' : ''} ${student.boardId ? 'has-board' : ''}`}
                        title={
                          student.boardId
                            ? `Решение на доске «${student.boardName ?? 'ДЗ'}»`
                            : student.needsReview
                              ? 'Отправлено на проверку'
                              : student.done
                                ? 'Сдано'
                                : 'Не сдано'
                        }
                      >
                        {student.boardId ? (
                          <Icon name="boards" size={11} />
                        ) : (
                          student.done && <Icon name="check" size={11} />
                        )}
                        {student.name}
                      </span>
                    ))}
                  </span>

                  {photos.length > 0 && (
                    <div className="hw-tile-photos" aria-hidden="true">
                      {photos.slice(0, 3).map((photo) => (
                        <img key={photo.id} src={photo.url} alt="" loading="lazy" />
                      ))}
                      {photos.length > 3 && <span>+{photos.length - 3}</span>}
                    </div>
                  )}

                  <span className="hw-tile-open">
                    {task.assignments.some((a) => a.needsReview)
                      ? 'Проверить решения'
                      : 'Открыть задание'}
                    <Icon name="chevronDown" size={14} className="rotate-270" />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
